// In-app property context strip rendered in the AppShell sticky header.
// See docs/design-system/guidelines/dashboards.md.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";

interface PropertyChipProps {
  propertyName: string;
  accountName?: string | null;
  /** Where the "Change property" link routes to. */
  changeHref?: string;
  /** Hide the inline "Change property" link (e.g. when the page header already has a Change CTA). */
  hideChange?: boolean;
  /** Optional left-side slot — used by AppShell to inline the mobile hamburger before the property name. */
  leading?: ReactNode;
  /** Optional right-side actions slot (date pickers, refresh, etc. in M5.x). */
  actions?: ReactNode;
}

export function PropertyChip({
  propertyName,
  accountName,
  changeHref = "/app/properties",
  hideChange = false,
  leading,
  actions,
}: PropertyChipProps) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
      <div className="flex min-w-0 items-center gap-3 text-sm">
        {leading}
        <span className="truncate font-display font-semibold text-neutral-950">{propertyName}</span>
        {accountName ? (
          <span className="hidden truncate text-neutral-500 sm:inline">· {accountName}</span>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {actions}
        {hideChange ? null : (
          <Link
            to={changeHref}
            className="inline-flex items-center gap-1 rounded text-xs font-semibold text-purple-700 hover:text-purple-900 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
          >
            <span className="hidden sm:inline">Change property</span>
            <span className="sm:hidden">Change</span>
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
          </Link>
        )}
      </div>
    </div>
  );
}
